import React, { useState, useEffect } from "react";
import { ArrowLeft, Loader2, LogOut } from "lucide-react";
import { subscriptionService } from "../services/subscription";
import { authService } from "../services/auth";
import { Toaster } from "./ui/toaster";
import PaymentForm from "./PaymentForm";
import { savingsService } from "../services/savings";
import { VondiLogo } from "./ui/VondiLogo";
import { BloqueBeneficio, BloqueCupon, BloquePrecio, precioConDescuento } from "./CheckoutVondi";
import { useLocation, useNavigate } from "react-router-dom";
import { useSubscriptionPayment } from "../hooks/useSubscriptionPayment";
import { leerCodigo, EVENTO_CODIGO } from "../lib/closerCode";
import { leerEnlace } from "../lib/enlace";
import { toast } from "../hooks/use-toast";
import BloqueCuenta, { faltaEnCuenta } from "./BloqueCuenta";

const SubscriptionRequired = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const {
    loading,
    error,
    loadPrice,
    plans,
    loadPlans,
    selectedPlan,
    setSelectedPlan,
    couponCode,
    setCouponCode,
    handlePayment,
  } = useSubscriptionPayment();

  const activePlan = plans.find((p) => p.key === selectedPlan);
  /* El ahorro del usuario alimenta el bloque de arriba y decide qué plan viene
     preseleccionado (Módulo 4, precio ancla dinámico). */
  const [ahorro, setAhorro] = useState(null);
  const [planFijado, setPlanFijado] = useState(false);
  /* Descuento validado del cupón: alimenta el precio tachado. Se borra al
     editar el código, para no enseñar un descuento que ya no corresponde. */
  const [descuento, setDescuento] = useState(null);

  /*
    Cuenta y pago en la misma pantalla (Ricardo, 16-sep-2026).

    Sin sesión, esta pantalla pide también los datos de la cuenta y la crea
    justo antes de cobrar, en el `beforeSubmit` del formulario de tarjeta: si el
    registro falla (un correo que ya existe, por ejemplo) no se toca la tarjeta
    ni se cobra nada.

    Registro y pago se habían separado porque, si la cuenta se creaba y el cobro
    fallaba, la persona quedaba con sesión a medias. Hoy eso ya no rompe nada:
    la home está abierta a cuentas gratuitas, y el enlace de referido queda
    atribuido al registrarse, así que un closer no pierde la venta si la persona
    paga después. Lo único que hay que cuidar es lo de abajo: en cuanto la cuenta
    existe, la pantalla pasa a modo «con sesión» y un reintento ya no intenta
    registrar otra vez.
  */
  const [conSesion, setConSesion] = useState(() => authService.isAuthenticated());
  const [cuenta, setCuenta] = useState({ name: "", email: "", phone: "", password: "" });

  const crearCuentaSiFalta = async () => {
    if (conSesion) return true;

    const falta = faltaEnCuenta(cuenta);
    if (falta) {
      toast({ title: "Revisa tus datos", description: falta, variant: "destructive" });
      return false;
    }

    try {
      const enlace = leerEnlace()?.codigo;
      const r = await authService.register({
        name: cuenta.name.trim(),
        email: cuenta.email.trim(),
        phone: cuenta.phone.trim(),
        password: cuenta.password,
        ...(enlace && { enlace }),
      });
      if (!r?.success) throw new Error(r?.error || "No se pudo crear tu cuenta.");
      setConSesion(true);
      return true;
    } catch (e) {
      const yaExiste = /registrad|already|existe/i.test(e.message || "");
      toast({
        title: yaExiste ? "Ese correo ya tiene cuenta" : "No se pudo crear tu cuenta",
        description: yaExiste ? "Inicia sesión y vuelve a esta pantalla para pagar." : e.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const irAIniciarSesion = () => navigate("/login", { state: { from: { pathname: "/subscription" } } });

  useEffect(() => {
    let vigente = true;
    savingsService
      .summary()
      .then((r) => vigente && setAhorro(r?.data ?? null))
      .catch(() => {});
    return () => { vigente = false; };
  }, []);

  /*
    El cupón que traía el enlace del closer, ya aplicado al llegar.

    Se guardó al abrir el enlace (`?code=`), en cualquier ruta, y aquí se
    recupera. Sin esto, quien entra por un enlace de venta tendría que volver a
    escribir a mano el código que ya le habían dado — y quien no se acuerde,
    paga el precio de lista y deja al closer sin comisión.
  */
  useEffect(() => {
    const aplicar = () => {
      const guardado = leerCodigo();
      if (!guardado?.code) return;

      setCouponCode(guardado.code);
      if (guardado.discount_percent > 0) {
        setDescuento({ ok: true, descuento: guardado.discount_percent, de: guardado.closer_name });
      }
    };

    aplicar();
    /* También si el código termina de validarse con la pantalla ya abierta —
       por ejemplo al entrar directo a `/subscription?code=…`. */
    window.addEventListener(EVENTO_CODIGO, aplicar);
    return () => window.removeEventListener(EVENTO_CODIGO, aplicar);
  }, [setCouponCode]);

  /*
    Qué plan viene marcado al llegar. Dos fuentes, y el orden importa.

    1. **Lo que eligió el usuario en la pantalla anterior.** `Prepago` manda el
       plan en `location.state` al deslizar y tocar comprar. Esto se ignoraba:
       alguien elegía mensual, tocaba, y aterrizaba aquí con el anual marcado.
       Elegir un plan y que la siguiente pantalla marque otro no es un detalle
       —es la app contradiciendo al usuario en la pantalla del cobro.

    2. **La sugerencia por ahorro** (Módulo 4, precio ancla dinámico), solo si
       no venía nada elegido.

    En los dos casos se hace una sola vez, para no pisar al usuario si cambia de
    plan aquí mismo.
  */
  useEffect(() => {
    if (planFijado || !plans.length) return;

    const elegido = location.state?.plan;
    if (elegido && plans.some((p) => p.key === elegido)) {
      setSelectedPlan(elegido);
      setPlanFijado(true);
      return;
    }

    if (!ahorro?.membership?.suggestedPlan) return;
    setSelectedPlan(ahorro.membership.suggestedPlan);
    setPlanFijado(true);
  }, [plans, ahorro, planFijado, setSelectedPlan, location.state]);

  const pesos = (n) => `$${Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;

  useEffect(() => {
    // Verificar si ya tiene suscripción activa al cargar
    checkSubscriptionStatus();
    loadPlans();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (selectedPlan) loadPrice(selectedPlan);
  }, [selectedPlan]); // eslint-disable-line

  /**
   * Verificar si el usuario ya tiene suscripción activa
   * Si la tiene, redirigir automáticamente a /home
   */
  const checkSubscriptionStatus = async () => {
    try {
      setCheckingSubscription(true);

      // Verificar autenticación
      if (!authService.isAuthenticated()) {
        setCheckingSubscription(false);
        return;
      }

      // Obtener usuario actualizado del backend
      const user = await authService.getCurrentUserFromBackend();

      if (!user) {
        setCheckingSubscription(false);
        return;
      }

      // Admins no necesitan suscripción
      if (user.role === "admin") {
        navigate("/admin");
        return;
      }

      // Verificar estado de suscripción
      try {
        const statusResponse = await subscriptionService.getStatus();

        const canAccessPremium =
          statusResponse?.data?.can_access_premium ??
          statusResponse?.data?.has_active_subscription ??
          statusResponse?.data?.is_active ??
          false;

        if (canAccessPremium) {
          // Ya tiene suscripción activa - redirigir a home
          navigate("/home", { replace: true });
          return;
        }
      } catch (error) {
        // Si no tiene suscripción, es normal, continuar mostrando el formulario
        console.log(
          "Usuario no tiene suscripción activa, mostrando formulario de pago",
        );
      }
    } catch (error) {
      console.error("Error verificando suscripción:", error);
    } finally {
      setCheckingSubscription(false);
    }
  };

  const handleLogout = () => {
    authService.logout();
    navigate("/login");
  };

  // Mostrar loading mientras se verifica la suscripción inicial
  if (checkingSubscription) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="text-vd-pizarra text-lg">Verificando suscripción...</div>
      </div>
    );
  }

  return (
    /*
      `PANTALLA_PAGO` / `PANTALLA_PAGO_SCROLL`, en su orden exacto:
      logo → beneficio → cupón → precio → tarjeta → términos → pagar.

      Apple Pay y Mercado Pago se retiraron por instrucción de Ricardo
      (11-ago-2026): por ahora solo tarjeta. Los mockups los muestran, pero
      ninguno existe todavía y un botón que no cobra pierde la venta ahí mismo.
    */
    <div className="min-h-screen bg-[#F5F5F7] px-6 py-8">
      <div className="max-w-md mx-auto">
        <Toaster />

        {/*
          Una salida que no sea cerrar sesión.

          Iván pidió «botón de return para home» (13-ago-2026). Hasta ahora la
          única puerta de esta pantalla era «cerrar sesión», que para quien
          acaba de registrarse es una salida absurda: le pide que abandone la
          cuenta que creó hace diez segundos por no querer pagar todavía. Y
          `/home` está abierta a las cuentas gratuitas desde el freemium, así
          que volver no lo deja en ningún sitio prohibido.
        */}
        <div className="flex items-center justify-between mb-6">
          {/* Sin sesión no hay home a la que volver: se vuelve a la tienda. */}
          <button
            onClick={() => navigate(conSesion ? '/home' : '/beneficios')}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-vd-linea text-vd-tinta transition-all active:scale-95"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Volver</span>
          </button>

          {conSesion && (
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-vd-linea text-vd-pizarra transition-all active:scale-95"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-medium">Cerrar sesión</span>
            </button>
          )}
        </div>

        {/* Logo en pastilla de vidrio */}
        <div className="flex justify-center mb-7">
          <div
            style={{
              background: 'rgba(255,255,255,0.92)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              border: '1.5px solid rgba(255,255,255,0.85)',
              borderRadius: 34,
              padding: '18px 46px',
              boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.8), 0 14px 34px -16px rgba(250,77,10,0.28)',
            }}
          >
            <VondiLogo size={38} withDescriptor />
          </div>
        </div>

        <BloqueBeneficio ahorro={ahorro} />

        <BloqueCupon
          valor={couponCode}
          onChange={(v) => { setCouponCode(v); setDescuento(null); }}
          aplicado={false}
          onValidado={setDescuento}
        />

        <BloquePrecio
          planes={plans}
          seleccionado={selectedPlan}
          onCambiar={setSelectedPlan}
          descuento={descuento}
        />

        {/* Procesando */}
        {loading && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-white rounded-3xl p-8 max-w-sm mx-4 text-center shadow-xl">
              <Loader2 className="w-14 h-14 text-[#F73E48] animate-spin mx-auto" />
              <h3 className="font-display text-vd-h3 text-vd-tinta mt-4">Procesando tu pago</h3>
              <p className="text-vd-pizarra text-sm mt-1">Un momento, no cierres esta pantalla.</p>
            </div>
          </div>
        )}

        {!conSesion && (
          <BloqueCuenta datos={cuenta} onChange={setCuenta} onIniciarSesion={irAIniciarSesion} />
        )}

        {/* Tarjeta + términos + PAGAR y ACCEDER */}
        <PaymentForm
          beforeSubmit={crearCuentaSiFalta}
          onSubmit={handlePayment}
          loading={loading}
          error={error}
          disabled={!selectedPlan}
          /* El importe REAL, con el cupón aplicado. Iba el precio de lista:
             hoy no se usa para nada, pero es el número que acabará viendo la
             persona en la hoja de pago del teléfono, y ahí un $650 con un
             cargo de $550 es una reclamación. */
          amount={precioConDescuento(activePlan, descuento)}
          currency={activePlan?.currency || 'MXN'}
          intervalo={activePlan?.interval}
          submitLabel={conSesion ? "PAGAR y ACCEDER" : "CREAR CUENTA Y PAGAR"}
        />
      </div>
    </div>
  );
};

export default SubscriptionRequired;
